import React, { Component, createRef } from 'react';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import axios from '../../shared/axios';

import withErrorHandler from '../../hoc/with-error-handler';

import { List } from 'antd';

import NotesHeader from './notes-header';
import NoteComponent from './note';

import { Note } from '../../shared/models';
import { NoteOwnerTypes, PolicyOriginTypes } from '../../shared/constants/enums';

import * as actions from './store/actions';
import * as sharedActions from '../../shared/store/actions';

export type NotesState = {
  eventsAdded: boolean;
  showArchive: boolean;
  compactMode: boolean;
  sortDirection: string;
  pageNumber: number;
  pageSize: number;
  defaultPageSize: number;
  notesPageRequest: {
    filters?: any;
    pageSize?: number;
    pageNumber?: number;
    showArchive?: boolean;
    compactMode?: boolean;
    sortDirection?: string;
    ignorePaging?: boolean;
    insuredId?: string | null;
    agencyId?: string | null;
    policyId?: string | null;
    taskId?: string | null;
    agentId?: string | null;
    opportunityId?: string | null;
    claimId?: string | null;
    serviceRequestId?: string | null;
    ownerType?: number;
    isHistoryNotes?: boolean;
    isSuperUserNotes?: boolean;
  };
  initialFilterValues: {
    notePolicyOrigins: PolicyOriginTypes[];
    noteTypeIds?: number[];
    userIds?: string[];
    createDate?: [dayjs.Dayjs, dayjs.Dayjs] | null;
    subject?: string;
  };
  notes: Note[];
  shouldReloadFileAssociations: boolean;
  searchSubjectString: string | null;
  isMomentumEnabled: boolean;
  isHistoryNotes?: boolean | undefined;
  isSuperUserNotes?: boolean;
  insuredId?: string | null;
  ownerType?: number | undefined;
  agencyId?: string | null;
  policyId?: string | null;
  taskId?: string | null;
  agentId?: string | null;
  opportunityId?: string | null;
  claimId?: string | null;
  serviceRequestId?: string | null;
};

interface NotesProps {
  notes?: {
    data?: Note[];
    totalCount?: number;
    totalChildrenCount?: number;
  };
  error?: any;
  loading?: boolean;
  notesFileAssociations?: any[];
  controlConfig?: any;
  controlConfigError?: any;
  controlConfigLoading?: boolean;
  insuredId?: string;
  agencyId?: string;
  policyId?: string;
  taskId?: string;
  agentId?: string;
  opportunityId?: string;
  claimId?: string;
  serviceRequestId?: string;
  ownerType?: number;
  isHistoryNotes?: boolean;
  isSuperUserNotes?: boolean;

  // Dispatch props
  getNoteTypes: (searchString?: string) => Promise<any>;
  getUsers: (searchString?: string) => Promise<any>;
  getNotesControlConfig: () => Promise<any>;
  saveNotesControlConfig: (controlConfig: any) => void;
  getNotes: (pageRequest: any) => Promise<any>;
  getNotesFileAssociations: (noteIds: string[]) => void;
  clearNotes: () => void;
  exportNotesToPdf: (notesPageRequest: any, fileName: string) => void;
  exportNotesToExcel: (notesPageRequest: any, fileName: string) => void;
}

class NotesComponent extends Component<NotesProps, NotesState> {
  private notesRef = createRef<HTMLDivElement>();

  state: NotesState = {
    eventsAdded: false,
    showArchive: false,
    compactMode: true,
    sortDirection: 'desc',
    pageNumber: 1,
    pageSize: 5,
    defaultPageSize: 5,
    notesPageRequest: {},
    initialFilterValues: {
      notePolicyOrigins: [
        PolicyOriginTypes.Manual,
        PolicyOriginTypes.DataImport,
        PolicyOriginTypes.NowCertsWebServices,
        PolicyOriginTypes.DataImportManual,
        PolicyOriginTypes.EDocs,
        PolicyOriginTypes.Tasks,
        PolicyOriginTypes.SMS,
        PolicyOriginTypes.Automate,
        PolicyOriginTypes.Momentum,
      ]
    },
    notes: [],
    shouldReloadFileAssociations: false,
    searchSubjectString: null,
    isMomentumEnabled: false,
  };

  componentDidMount(): void {
    if ((this.props.notes?.data || []).length) {
      this.props.clearNotes();
    }

    this.props.getNotesControlConfig().then((res) => {
      const controlConfig = res;

      this.setState(
        {
          showArchive: controlConfig.showArchive,
          compactMode: controlConfig.compactMode,
          defaultPageSize: controlConfig.defaultPageCount,
          pageSize: controlConfig.defaultPageCount,
          isMomentumEnabled: controlConfig.isMomentumEnabled,
          initialFilterValues: {
            notePolicyOrigins: controlConfig.notePolicyOrigins,
            noteTypeIds: controlConfig.noteTypeIds ? controlConfig.noteTypeIds : [],
            userIds: controlConfig.userIds ? controlConfig.userIds : [],
            createDate: this.props.isHistoryNotes === true ? [dayjs().utc().subtract(1, 'day').startOf('day'), dayjs().utc().endOf('day')] : null,
          },
        },
        () => {
          if (controlConfig.noteTypeIds && controlConfig.noteTypeIds.length) {
            this.props.getNoteTypes();
          }

          if (controlConfig.userIds && controlConfig.userIds.length) {
            this.props.getUsers();
          }

          this.loadData(this.state.initialFilterValues);
        },
      );
    });
  }

  componentDidUpdate(prevProps: NotesProps, prevState: NotesState): void {
    if (!this.state.eventsAdded) {
      this.stopEventsPropagation();
    }

    if (JSON.stringify(prevProps.notes?.data) !== JSON.stringify(this.props.notes?.data) || this.state.shouldReloadFileAssociations) {
      this.setState({ shouldReloadFileAssociations: false }, () => {
        this.setState({ notes: this.props.notes?.data || [] }, () => {
          this.props.getNotesFileAssociations(this.state.notes.map((x) => x.id));
        });
      });
    }
  }

  stopEventsPropagation = (): void => {
    const notesDocument = this.notesRef.current;
    if (!notesDocument) return;

    const elementsToPrevent = notesDocument.querySelectorAll('div.ant-pagination-options-quick-jumper input');

    if (!elementsToPrevent || elementsToPrevent.length === 0) return;

    const eventsToPrevent = ['keydown', 'keypress'];

    const handler = (event: KeyboardEvent): boolean => {
      const key = event.key || event.keyCode;
  
      if (key === 'Enter' || key === 13) {
        event.stopPropagation();
        event.preventDefault();
        return false;
      }
      return true;
    };

    elementsToPrevent.forEach((element) => {
      eventsToPrevent.forEach((eventName) => {
        element.addEventListener(eventName, handler as any, true);
      });
    });

    this.setState({ eventsAdded: true });
  };

  getBaseRequestParams = (): {
    insuredId: string | null;
    ownerType: number | undefined;
    agentId: string | null;
    agencyId: string | null;
    policyId: string | null;
    taskId: string | null;
    opportunityId: string | null;
    claimId: string | null;
    serviceRequestId: string | null;
  } => {
    const baseRequestParams = {
      insuredId: null as string | null,
      ownerType: this.state.ownerType,
      agentId: null as string | null,
      agencyId: null as string | null,
      policyId: null as string | null,
      taskId: null as string | null,
      opportunityId: null as string | null,
      claimId: null as string | null,
      serviceRequestId: null as string | null,
    };

    switch (baseRequestParams.ownerType) {
      case NoteOwnerTypes.Insured:
        baseRequestParams.insuredId = this.state.insuredId || null;
        break;
      case NoteOwnerTypes.Agent:
        baseRequestParams.agentId = this.state.agentId || null;
        break;
      case NoteOwnerTypes.Agency:
        baseRequestParams.agencyId = this.state.agencyId || null;
        break;
      case NoteOwnerTypes.Policy:
        baseRequestParams.insuredId = this.state.insuredId || null;
        baseRequestParams.policyId = this.state.policyId || null;
        break;
      case NoteOwnerTypes.Task:
        baseRequestParams.insuredId = this.state.insuredId || null;
        baseRequestParams.taskId = this.state.taskId || null;
        break;
      case NoteOwnerTypes.Opportunity:
        baseRequestParams.insuredId = this.state.insuredId || null;
        baseRequestParams.opportunityId = this.state.opportunityId || null;
        break;
      case NoteOwnerTypes.Claim:
        baseRequestParams.insuredId = this.state.insuredId || null;
        baseRequestParams.claimId = this.state.claimId || null;
        break;
      case NoteOwnerTypes.ServiceRequest:
        baseRequestParams.insuredId = this.state.insuredId || null;
        baseRequestParams.serviceRequestId = this.state.serviceRequestId || null;
        break;
      case NoteOwnerTypes.SuperUserAgencyNotes:
        baseRequestParams.agencyId = this.state.agencyId || null;
        break;
    }

    return baseRequestParams;
  };

  loadData = (filters: any): void => {
    const insuredId = this.props.insuredId;
    const agencyId = this.props.agencyId;
    const policyId = this.props.policyId;
    const taskId = this.props.taskId;
    const agentId = this.props.agentId;
    const opportunityId = this.props.opportunityId;
    const claimId = this.props.claimId;
    const serviceRequestId = this.props.serviceRequestId;
    const ownerType = Number(this.props.ownerType);
    const isHistoryNotes = this.props.isHistoryNotes === true;
    const isSuperUserNotes = this.props.isSuperUserNotes === true;

    this.setState(
      {
        notesPageRequest: {
          filters: filters,
          pageSize: this.state.pageSize,
          pageNumber: this.state.pageNumber,
          showArchive: this.state.showArchive,
          compactMode: this.state.compactMode,
          sortDirection: this.state.sortDirection,
          ignorePaging: false,
          insuredId,
          agencyId,
          policyId,
          taskId,
          agentId,
          opportunityId,
          claimId,
          serviceRequestId,
          ownerType,
          isHistoryNotes,
          isSuperUserNotes,
        },
        isHistoryNotes,
        isSuperUserNotes,
        insuredId,
        ownerType,
        agencyId,
        policyId,
        taskId,
        agentId,
        opportunityId,
        claimId,
        serviceRequestId,
        searchSubjectString: filters?.subject,
      },
      () => this.props.getNotes(this.state.notesPageRequest),
    );
  };

  reloadData = (): void => {
    if (this.state.notesPageRequest && this.state.notesPageRequest.filters) {
      this.loadData(this.state.notesPageRequest.filters);
    } else {
      this.loadData(null);
    }
  };

  fetchFilteredData = (filter: any): void => {
    this.setState({ pageNumber: 1 }, () => {
      if (this.controlConfigShouldBeSaved(filter)) {
        this.saveControlConfig(filter);
      }

      this.loadData(filter);
    });
  };

  controlConfigShouldBeSaved = (filter: any): boolean => {
    const updatedNotePolicyOrigins = filter.notePolicyOrigins;
    const currentNotePolicyOrigins = this.state.notesPageRequest.filters?.notePolicyOrigins || [];

    const updatedNoteTypeIds = filter.noteTypeIds;
    const currentNoteTypeIds = this.state.notesPageRequest.filters?.noteTypeIds || [];

    const updatedUserIds = filter.userIds;
    const currentUserIds = this.state.notesPageRequest.filters?.userIds || [];

    const originsChanged =
      updatedNotePolicyOrigins.length !== currentNotePolicyOrigins.length ||
      !updatedNotePolicyOrigins.every((value: number, index: number) => value === currentNotePolicyOrigins[index]);

    const typesChanged =
      !currentNoteTypeIds ||
      updatedNoteTypeIds.length !== currentNoteTypeIds.length ||
      !updatedNoteTypeIds.every((value: number, index: number) => value === currentNoteTypeIds[index]);

    const usersChanged =
      !currentUserIds || updatedUserIds.length !== currentUserIds.length || !updatedUserIds.every((value: string, index: number) => value === currentUserIds[index]);

    return originsChanged || typesChanged || usersChanged;
  };

  saveControlConfig = (filters?: any): void => {
    this.props.saveNotesControlConfig({
      showArchive: this.state.showArchive,
      compactMode: this.state.compactMode,
      notePolicyOrigins: filters ? filters.notePolicyOrigins : this.state.initialFilterValues.notePolicyOrigins,
      noteTypeIds: filters ? filters.noteTypeIds : null,
      userIds: filters ? filters.userIds : null,
    });
  };

  handlePagingChange = (pageSize: number, pageNumber: number): void => {
    this.setState({ pageSize: pageSize, pageNumber: pageNumber }, () => {
      this.reloadData();
    });
  };

  handleShowArchiveChange = (value: boolean): void => {
    this.setState({ showArchive: value, pageNumber: 1 }, () => {
      this.saveControlConfig(this.state.notesPageRequest.filters);

      this.reloadData();
    });
  };

  handleCompactModeChange = (value: boolean): void => {
    this.setState({ compactMode: value }, () => {
      this.saveControlConfig(this.state.notesPageRequest.filters);

      this.reloadData();
    });
  };

  handleSortChange = (dir: string): void => {
    this.setState({ sortDirection: dir }, () => {
      this.reloadData();
    });
  };

  refresh = (): void => {
    this.setState({ shouldReloadFileAssociations: true }, () => {
      this.reloadData();
    });
  };

  handleRefreshAfterArchiveOrReassign = (isArchiving: boolean): void => {
    const totalCount = this.props.notes?.totalCount || 0;
    const { pageSize, pageNumber } = this.state;

    const pageDelta = (totalCount - (isArchiving ? 1 : 0)) % pageSize;
    const lastPage = (totalCount - (isArchiving ? 1 : 0)) / pageSize === pageNumber - 1;
    if (pageDelta === 0 && lastPage) {
      this.setState({ pageNumber: pageNumber !== 1 ? pageNumber - 1 : 1, shouldReloadFileAssociations: true }, () => {
        this.reloadData();
      });
    } else {
      this.setState({ shouldReloadFileAssociations: true }, () => {
        this.reloadData();
      });
    }
  };

  handleRefreshAfterCreate = (): void => {
    this.setState({ pageNumber: 1, shouldReloadFileAssociations: true }, () => this.reloadData());
  };

  renderTotal = (total: number, range: [number, number]): React.ReactNode => {
    const totalChildren = this.props?.notes?.totalChildrenCount || 0;
    return (
      <>
        {totalChildren} comments total, {range[0]}-{range[1]} of {total} notes
      </>
    );
  };

  exportNotesToPdf = (): void => {
    const fileName = this.getFileNameForNotesExport();

    this.props.exportNotesToPdf({ ...this.state.notesPageRequest }, fileName);
  };

  exportNotesToExcel = (): void => {
    const fileName = this.getFileNameForNotesExport();

    this.props.exportNotesToExcel({ ...this.state.notesPageRequest }, fileName);
  };

  getFileNameForNotesExport = (): string => {
    if (this.state.isSuperUserNotes) {
      return 'SuperUserNotes';
    }

    if (this.state.isHistoryNotes) {
      return 'HistoryNotes';
    }

    switch (this.state.ownerType) {
      case NoteOwnerTypes.Insured:
        return `NotesForInsured`;
      case NoteOwnerTypes.Agent:
        return `NotesForAgent`;
      case NoteOwnerTypes.Agency:
        return `NotesForAgency`;
      case NoteOwnerTypes.Policy:
        return `NotesForPolicy`;
      case NoteOwnerTypes.Task:
        return `NotesForTasks`;
      case NoteOwnerTypes.Opportunity:
        return `NotesForOpportunity`;
      case NoteOwnerTypes.Claim:
        return `NotesForClaim`;
      case NoteOwnerTypes.ServiceRequest:
        return `NotesForRequest`;
      default:
        return 'Notes';
    }
  };

  render(): React.ReactNode {
    const { loading, controlConfigLoading } = this.props;
    const {
      compactMode,
      showArchive,
      isHistoryNotes,
      isSuperUserNotes,
      ownerType,
      insuredId,
      agencyId,
      policyId,
      taskId,
      pageSize,
      defaultPageSize,
      pageNumber,
      agentId,
      initialFilterValues,
      opportunityId,
      claimId,
      serviceRequestId,
      searchSubjectString,
      isMomentumEnabled,
    } = this.state;
    return (
      <List
        ref={this.notesRef}
        bordered
        size="small"
        loading={loading || controlConfigLoading}
        dataSource={this.state.notes || []}
        className="notes-list"
        header={
          <NotesHeader
            compactModeEnabled={compactMode}
            showArchiveEnabled={showArchive}
            isMomentumEnabled={isMomentumEnabled}
            showArchiveChange={(value) => this.handleShowArchiveChange(value)}
            compactModeChange={(value) => this.handleCompactModeChange(value)}
            sortChange={(value) => this.handleSortChange(value)}
            refresh={() => this.handleRefreshAfterCreate()}
            fetchFilteredData={(filters) => this.fetchFilteredData(filters)}
            reloadData={() => this.refresh()}
            exportNotesToPdf={this.exportNotesToPdf}
            exportNotesToExcel={this.exportNotesToExcel}
            baseRequestParams={this.getBaseRequestParams()}
            isHistoryNotes={isHistoryNotes}
            isSuperUserNotes={isSuperUserNotes}
            initialFilterValues={initialFilterValues}
          />
        }
        renderItem={(item) => (
          <NoteComponent
            key={item.id}
            item={item}
            fileAssociations={this.props.notesFileAssociations}
            compactModeEnabled={compactMode}
            showArchiveEnabled={showArchive}
            refresh={() => this.refresh()}
            refreshAfterArchiveOrReassign={(isArchiving) => this.handleRefreshAfterArchiveOrReassign(isArchiving)}
            baseRequestParams={this.getBaseRequestParams()}
            ownerType={ownerType}
            insuredId={insuredId}
            agencyId={agencyId}
            policyId={policyId}
            taskId={taskId}
            agentId={agentId}
            isHistoryNotes={isHistoryNotes}
            isSuperUserNotes={isSuperUserNotes}
            opportunityId={opportunityId}
            claimId={claimId}
            serviceRequestId={serviceRequestId}
            searchString={searchSubjectString}
          />
        )}
        pagination={{
          size: 'small',
          showSizeChanger: true,
          showQuickJumper: true,
          defaultPageSize: defaultPageSize,
          pageSize: pageSize,
          current: pageNumber,
          total: this.props?.notes?.totalCount || 0,
          pageSizeOptions: ['5', '10', '20', '50'],
          onChange: (page, pageSize) => this.handlePagingChange(pageSize as number, page),
          showTotal: (total, range) => this.renderTotal(total, range as [number, number]),
        }}
      />
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    notes: state.notes.notes,
    error: state.notes.error,
    loading: state.notes.loading,
    notesFileAssociations: state.notes.notesFileAssociations,
    controlConfig: state.notes.controlConfig,
    controlConfigError: state.notes.controlConfigError,
    controlConfigLoading: state.notes.controlConfigLoading,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    getNoteTypes: (searchString?: string) => dispatch(actions.getNoteTypes(searchString)),
    getUsers: (searchString?: string) => dispatch(sharedActions.getUsers(searchString)),
    getNotesControlConfig: () => dispatch(actions.getNotesControlConfig()),
    saveNotesControlConfig: (controlConfig: any) => dispatch(actions.saveNotesControlConfig(controlConfig)),
    getNotes: (pageRequest: any) => dispatch(actions.getNotes(pageRequest)),
    getNotesFileAssociations: (noteIds: string[]) => dispatch(actions.getNotesFileAssociations(noteIds)),
    clearNotes: () => dispatch(actions.clearNotes()),
    exportNotesToPdf: (notesPageRequest: any, fileName: string) => dispatch(actions.exportNotesToPdf(notesPageRequest, fileName)),
    exportNotesToExcel: (notesPageRequest: any, fileName: string) => dispatch(actions.exportNotesToExcel(notesPageRequest, fileName)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withErrorHandler(NotesComponent, axios));
